export const CHECKOUT_WINDOW_FEATURES = [
	"width=600",
	"height=800",
	`left=${window.screen.width / 2 - 300}`,
	`top=${window.screen.height / 2 - 400}`,
].join(",");

/**
 * Opens the checkout popup up front, before any request is awaited — Safari and
 * Firefox block `window.open` once the click handler has yielded. Returns null
 * when the browser blocks it, in which case the caller should fall back to a
 * same-tab redirect.
 */
export function preOpenCheckoutWindow(name: string): Window | null {
	try {
		const tab = window.open("about:blank", name, CHECKOUT_WINDOW_FEATURES);
		if (!tab || tab.closed) {
			return null;
		}
		return tab;
	} catch {
		return null;
	}
}

export function closeCheckoutWindow(checkoutWindow: Window | null): void {
	if (!checkoutWindow || checkoutWindow.closed) {
		return;
	}
	try {
		checkoutWindow.close();
	} catch {
		// The opener can lose access if the user already navigated the tab.
	}
}

/**
 * Send a pre-opened checkout tab to Stripe after the session URL is known.
 *
 * Never calls `window.open` here — that would run after an await and get
 * blocked. If the popup was never created, navigate this tab instead. If the
 * user closed the blank tab while the request was in flight, leave this tab
 * alone so we do not hijack the page.
 *
 * Returns the popup when it is still in use (including already-closed, so
 * callers can reset UI). Returns null when this tab is navigating away.
 */
export function navigateCheckoutWindow(
	checkoutWindow: Window | null,
	url: string,
): Window | null {
	if (!checkoutWindow) {
		window.location.assign(url);
		return null;
	}

	if (checkoutWindow.closed) {
		return checkoutWindow;
	}

	try {
		checkoutWindow.location.replace(url);
	} catch (error) {
		// Closed between the check and replace — treat as a dismiss, same as
		// the explicit `.closed` path above. Do not hijack this tab.
		if (checkoutWindow.closed) {
			return checkoutWindow;
		}
		// Still open but we cannot navigate it (named window already on Stripe).
		console.warn(
			"Checkout popup navigation failed, falling back to same-tab redirect",
			error,
		);
		closeCheckoutWindow(checkoutWindow);
		window.location.assign(url);
		return null;
	}

	try {
		checkoutWindow.focus();
	} catch {
		// Focus is optional; the tab already has the checkout URL.
	}
	return checkoutWindow;
}

/**
 * Closing the popup with the window chrome sends no message back to the opener,
 * so a checkout that is simply dismissed would otherwise leave the button in a
 * permanent "purchasing" state. Polling `closed` is the only cross-origin
 * signal available. Returns a cleanup function.
 */
export function watchCheckoutWindowClose(
	checkoutWindow: Window | null,
	onClose: () => void,
): () => void {
	if (!checkoutWindow) return () => {};

	if (checkoutWindow.closed) {
		onClose();
		return () => {};
	}

	const watcher = window.setInterval(() => {
		if (!checkoutWindow.closed) return;
		window.clearInterval(watcher);
		onClose();
	}, 500);

	return () => window.clearInterval(watcher);
}
